import * as React from 'react'

import { Channel, GroupChannel } from '@/types/group-channel.type'

type SidebarGroup = GroupChannel & { isExpanded: boolean }

interface UseChannelSelectionProps {
  currentChannelId?: string
  groupId?: string
  groups: SidebarGroup[]
  ungroupedChannels?: Channel[]
  onSelectChannelProp?: (channelId: string, groupId: string, channelName: string) => void
  autoSelectFirst?: boolean
  loading?: boolean
}

export function useChannelSelection({
  currentChannelId,
  groupId,
  groups,
  ungroupedChannels = [],
  onSelectChannelProp,
  autoSelectFirst = true,
  loading = false,
}: UseChannelSelectionProps) {
  const [activeChannelId, setActiveChannelId] = React.useState<string | null>(currentChannelId || null)
  const [hasInitialized, setHasInitialized] = React.useState(false)

  React.useEffect(() => {
    if (loading) return

    const timer = setTimeout(() => {
      if (!hasInitialized && autoSelectFirst && !currentChannelId && !groupId && onSelectChannelProp) {
        if (ungroupedChannels.length > 0) {
          const firstChannel = ungroupedChannels[0]
          setActiveChannelId(firstChannel.id)
          onSelectChannelProp(firstChannel.id, 'ungrouped', firstChannel.name)
          setHasInitialized(true)
        } else if (groups.length > 0) {
          const firstGroup = groups.find(g => (g.channels || []).length > 0)
          const firstChannel = firstGroup?.channels[0]
          if (firstChannel && firstGroup) {
            setActiveChannelId(firstChannel.id)
            onSelectChannelProp(firstChannel.id, firstGroup.id, firstChannel.name)
            setHasInitialized(true)
          } else {
            setHasInitialized(true)
          }
        } else {
          setHasInitialized(true)
        }
      } else if (currentChannelId || groupId) {
        setHasInitialized(true)
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [
    currentChannelId,
    groupId,
    groups,
    ungroupedChannels,
    onSelectChannelProp,
    hasInitialized,
    autoSelectFirst,
    loading,
  ])

  React.useEffect(() => {
    if (currentChannelId && currentChannelId !== activeChannelId) {
      setActiveChannelId(currentChannelId)
    }
  }, [currentChannelId, activeChannelId, groupId])

  const onSelectChannelId = React.useCallback(
    (channel: Channel, groupId: string) => {
      setActiveChannelId(channel.id)
      if (onSelectChannelProp) onSelectChannelProp(channel.id, groupId, channel.name)
    },
    [onSelectChannelProp]
  )

  return {
    activeChannelId,
    onSelectChannelId,
  }
}
